import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { capitalizeWords, formatDate } from '../../utils/helper';
import {
  ServiceReportAc,
  UpdateServiceReport,
} from '../../services/serviceReport';
import useSnackbar from '../../hooks/useSnackbar';
import { PrimaryButton, SecondaryButton } from '../atoms/Button';

interface BookingAccordionProps {
  ac: ServiceReportAc;
  index: number;
  editable?: boolean;
  onFieldChange?: (index: number, field: keyof ServiceReportAc, value: unknown) => void;
  onMaterialChange?: (
    acIndex: number,
    materialIndex: number,
    field: 'qty' | 'rate',
    value: number,
  ) => void;
}

interface ImageViewerProps {
  open: boolean;
  imageSrc: string;
  onClose: () => void;
}

const fieldInputSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    backgroundColor: '#181818',
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#444',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#666',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E31E25',
  },
  '& .MuiInputLabel-root': {
    color: '#9ca3af',
  },
} as const;

const ImageViewer: React.FC<ImageViewerProps> = ({ open, imageSrc, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setScale((prev) => Math.min(prev + 0.1, 4));
    } else {
      setScale((prev) => Math.max(prev - 0.1, 0.5));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          maxWidth: '95vw',
          maxHeight: '95vh',
          m: 2,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          display: 'flex',
          gap: 1,
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          borderRadius: 2,
          p: 0.5,
        }}
      >
        <IconButton onClick={handleZoomIn} sx={{ color: '#fff' }} title="Zoom In">
          <ZoomInIcon />
        </IconButton>
        <IconButton onClick={handleZoomOut} sx={{ color: '#fff' }} title="Zoom Out">
          <ZoomOutIcon />
        </IconButton>
        <IconButton onClick={handleReset} sx={{ color: '#fff' }} title="Reset">
          <RestartAltIcon />
        </IconButton>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }} title="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography
        sx={{
          position: 'absolute',
          bottom: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#fff',
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          px: 2,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.875rem',
        }}
      >
        {Math.round(scale * 100)}% | Scroll to zoom, drag to pan
      </Typography>
      <DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          p: 0,
          minHeight: '80vh',
          cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <Box
          component="img"
          src={imageSrc}
          alt="Full view"
          sx={{
            maxWidth: '100%',
            maxHeight: '85vh',
            objectFit: 'contain',
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

const serviceReportAccordionSx = {
  background: '#222',
  color: '#fff',
  borderRadius: '8px',
  mb: 2,
  border: '1px solid #333',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: 0,
    mb: 2,
    borderColor: '#3E3E3E',
  },
} as const;

const serviceReportAccordionSummarySx = {
  minHeight: 48,
  px: 1.5,
  '& .MuiAccordionSummary-content': {
    my: 1,
    alignItems: 'center',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
} as const;

const getServiceTypeLabel = (serviceType?: string) => {
  if (!serviceType) return 'N/A';
  return capitalizeWords(serviceType.replaceAll('_', ' '));
};

const DetailField = ({
  label,
  value,
  editable,
  children,
}: {
  label: string;
  value?: React.ReactNode;
  editable?: boolean;
  children?: React.ReactNode;
}) => (
  <Grid item xs={12} sm={6}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>{label}</Typography>
      {editable && children ? (
        children
      ) : (
        <Typography sx={{ color: '#FFFFFF' }}>{value ?? 'N/A'}</Typography>
      )}
    </Box>
  </Grid>
);

const BookingAccordion: React.FC<BookingAccordionProps> = ({
  ac = {},
  index,
  editable = false,
  onFieldChange,
  onMaterialChange,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (img: string) => {
    setSelectedImage(img);
  };

  const handleCloseViewer = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <ImageViewer
        open={!!selectedImage}
        imageSrc={selectedImage || ''}
        onClose={handleCloseViewer}
      />
      <Accordion sx={serviceReportAccordionSx}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#8A8D91' }} />}
          sx={serviceReportAccordionSummarySx}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#0BE600',
              fontWeight: 600,
              minWidth: 120,
            }}
          >
            {getServiceTypeLabel(ac?.serviceType)}
          </Typography>
          {ac?.jobStatus ? (
            <Typography
              variant="body2"
              sx={{ color: '#FFFFFF', ml: 2, fontWeight: 500 }}
            >
              {capitalizeWords(String(ac.jobStatus).replaceAll('_', ' '))}
            </Typography>
          ) : null}
        </AccordionSummary>

        <AccordionDetails>
          <Typography variant="h6" sx={{ mb: 2, color: 'red' }}>
            AC Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <DetailField
              label="Service Type"
              value={getServiceTypeLabel(ac?.serviceType)}
            />
            <DetailField
              label="Job Status"
              editable={editable}
              value={capitalizeWords(String(ac?.jobStatus || 'N/A').replaceAll('_', ' '))}
            >
              <TextField
                select
                size="small"
                fullWidth
                value={ac?.jobStatus || 'PENDING'}
                onChange={(event) =>
                  onFieldChange?.(index, 'jobStatus', event.target.value)
                }
                SelectProps={{ native: true }}
                sx={fieldInputSx}
              >
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
              </TextField>
            </DetailField>
            <DetailField label="AC Type" value={ac?.acType || 'N/A'} />
            <DetailField
              label="Brand"
              editable={editable}
              value={ac?.brandName || 'N/A'}
            >
              <TextField
                size="small"
                fullWidth
                value={ac?.brandName || ''}
                onChange={(event) =>
                  onFieldChange?.(index, 'brandName', event.target.value)
                }
                sx={fieldInputSx}
              />
            </DetailField>
            <DetailField
              label="Year Of Manufacture"
              editable={editable}
              value={ac?.yom ?? 'N/A'}
            >
              <TextField
                size="small"
                fullWidth
                type="number"
                value={ac?.yom ?? ''}
                onChange={(event) =>
                  onFieldChange?.(index, 'yom', Number(event.target.value))
                }
                sx={fieldInputSx}
              />
            </DetailField>
            <DetailField
              label="Tonnage"
              editable={editable}
              value={ac?.tr ?? 'N/A'}
            >
              <TextField
                size="small"
                fullWidth
                type="number"
                inputProps={{ step: 0.1, min: 0 }}
                value={ac?.tr ?? ''}
                onChange={(event) =>
                  onFieldChange?.(index, 'tr', Number(event.target.value))
                }
                sx={fieldInputSx}
              />
            </DetailField>
            <DetailField
              label="Inverter"
              editable={editable}
              value={ac?.inverter ? 'Yes' : 'No'}
            >
              <TextField
                select
                size="small"
                fullWidth
                value={ac?.inverter ? 'yes' : 'no'}
                onChange={(event) =>
                  onFieldChange?.(index, 'inverter', event.target.value === 'yes')
                }
                SelectProps={{ native: true }}
                sx={fieldInputSx}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </TextField>
            </DetailField>
            <DetailField
              label="Completed At"
              value={ac?.completedAt ? formatDate(ac.completedAt) : 'N/A'}
            />
          </Grid>

          {(ac?.materials?.length ?? 0) > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: 'red' }}>
                Material Details
              </Typography>
              <Divider sx={{ bgcolor: '#424242', mb: 2 }} />
              <Grid container spacing={2}>
                {ac.materials?.map((item, materialIndex) => {
                  const qty = Number(item?.qty ?? item?.quantity ?? 0);
                  const rate = Number(item?.rate ?? 0);
                  const amount = qty * rate;
                  const userRate = Number(item?.userRate ?? item?.rate ?? 0);
                  const userAmount = Number(item?.userAmount ?? qty * userRate);

                  return (
                    <Grid item xs={12} key={`${item?.name}-${materialIndex}`}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ color: '#BDBDBD', fontWeight: 600 }}>
                          {item?.name || 'N/A'}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                              Quantity
                            </Typography>
                            {editable ? (
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0, step: 1 }}
                                value={qty}
                                onChange={(event) =>
                                  onMaterialChange?.(
                                    index,
                                    materialIndex,
                                    'qty',
                                    Number(event.target.value),
                                  )
                                }
                                sx={{ ...fieldInputSx, mt: 0.5 }}
                              />
                            ) : (
                              <Typography sx={{ color: '#FFFFFF' }}>
                                {qty} {item?.unit || ''}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                              Rate
                            </Typography>
                            {editable ? (
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                value={rate}
                                onChange={(event) =>
                                  onMaterialChange?.(
                                    index,
                                    materialIndex,
                                    'rate',
                                    Number(event.target.value),
                                  )
                                }
                                sx={{ ...fieldInputSx, mt: 0.5 }}
                              />
                            ) : (
                              <Typography sx={{ color: '#FFFFFF' }}>
                                {rate}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                              Amount
                            </Typography>
                            <Typography sx={{ color: '#FFFFFF' }}>
                              {editable ? amount.toFixed(2) : (item?.amount ?? amount)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                              Technician Rate
                            </Typography>
                            <Typography sx={{ color: '#FFFFFF' }}>
                              {userRate}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                              Technician Amount
                            </Typography>
                            <Typography sx={{ color: '#FFFFFF' }}>
                              {userAmount.toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              <Divider sx={{ bgcolor: '#424242', mt: 2, mb: 2 }} />
            </>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'red' }}>
              Remark
            </Typography>
            {editable ? (
              <TextField
                multiline
                rows={3}
                fullWidth
                value={ac?.remark || ''}
                onChange={(event) =>
                  onFieldChange?.(index, 'remark', event.target.value)
                }
                sx={fieldInputSx}
              />
            ) : (
              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                {ac?.remark || 'N/A'}
              </Typography>
            )}
          </Box>

          {ac?.beforePhotos && ac.beforePhotos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: '#fff' }}>
                Before Photos
              </Typography>
              <Grid container spacing={2}>
                {ac.beforePhotos.map((img, photoIndex) => (
                  <Grid item xs={12} sm={6} md={3} key={`before-${photoIndex}`}>
                    <Box
                      onClick={() => handleImageClick(img)}
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': { '& .zoom-overlay': { opacity: 1 } },
                      }}
                    >
                      <Box
                        component="img"
                        src={img}
                        alt={`Before ${photoIndex}`}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 2,
                        }}
                      />
                      <Box
                        className="zoom-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0, 0, 0, 0.4)',
                          borderRadius: 2,
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <ZoomInIcon sx={{ color: '#fff', fontSize: 40 }} />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {ac?.afterPhotos && ac.afterPhotos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: '#fff' }}>
                After Photos
              </Typography>
              <Grid container spacing={2}>
                {ac.afterPhotos.map((img, photoIndex) => (
                  <Grid item xs={12} sm={6} md={3} key={`after-${photoIndex}`}>
                    <Box
                      onClick={() => handleImageClick(img)}
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': { '& .zoom-overlay': { opacity: 1 } },
                      }}
                    >
                      <Box
                        component="img"
                        src={img}
                        alt={`After ${photoIndex}`}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 2,
                        }}
                      />
                      <Box
                        className="zoom-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0, 0, 0, 0.4)',
                          borderRadius: 2,
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <ZoomInIcon sx={{ color: '#fff', fontSize: 40 }} />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

type PaymentDraft = {
  totalAmount: number | '';
  paidAmount: number | '';
  paymentStatus: 'COMPLETE' | 'PARTIAL' | 'PENDING';
};

export const ACServiceDetails = ({
  booking,
  onReload,
}: {
  booking: { _id?: string; serviceReport?: Record<string, unknown> };
  onReload?: () => void;
}) => {
  const serviceReport = booking?.serviceReport;
  const acs = (serviceReport?.acs as ServiceReportAc[]) || [];
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftAcs, setDraftAcs] = useState<ServiceReportAc[]>([]);
  const [draftPayment, setDraftPayment] = useState<PaymentDraft>({
    totalAmount: '',
    paidAmount: '',
    paymentStatus: 'PENDING',
  });

  useEffect(() => {
    if (!isEditing) return;
    setDraftAcs(JSON.parse(JSON.stringify(acs)));
    setDraftPayment({
      totalAmount:
        typeof serviceReport?.totalAmount === 'number'
          ? serviceReport.totalAmount
          : '',
      paidAmount:
        typeof serviceReport?.paidAmount === 'number'
          ? serviceReport.paidAmount
          : '',
      paymentStatus:
        (serviceReport?.paymentStatus as PaymentDraft['paymentStatus']) ||
        'PENDING',
    });
  }, [isEditing, booking?._id, serviceReport]);

  const totalAmount =
    typeof serviceReport?.totalAmount === 'number'
      ? serviceReport.totalAmount
      : undefined;
  const paidAmount =
    typeof serviceReport?.paidAmount === 'number'
      ? serviceReport.paidAmount
      : undefined;
  const dueAmount =
    typeof totalAmount === 'number' && typeof paidAmount === 'number'
      ? Math.max(0, totalAmount - paidAmount)
      : null;
  // Total of the technician-entered material amounts; older reports without
  // the stored field fall back to summing per-material userAmount/rate.
  const totalUserMaterialAmount =
    typeof serviceReport?.totalUserMaterialAmount === 'number'
      ? serviceReport.totalUserMaterialAmount
      : acs.reduce(
          (sum, ac) =>
            sum +
            (ac.materials || []).reduce((acSum, material) => {
              const qty = Number(material.qty ?? material.quantity ?? 0);
              const userRate = Number(material.userRate ?? material.rate ?? 0);
              return acSum + Number(material.userAmount ?? qty * userRate);
            }, 0),
          0,
        );

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraftAcs([]);
  };

  const handleFieldChange = (
    index: number,
    field: keyof ServiceReportAc,
    value: unknown,
  ) => {
    setDraftAcs((prev) =>
      prev.map((ac, acIndex) =>
        acIndex === index ? { ...ac, [field]: value } : ac,
      ),
    );
  };

  const handleMaterialChange = (
    acIndex: number,
    materialIndex: number,
    field: 'qty' | 'rate',
    value: number,
  ) => {
    setDraftAcs((prev) =>
      prev.map((ac, currentAcIndex) => {
        if (currentAcIndex !== acIndex) return ac;

        const materials = (ac.materials || []).map((material, currentMaterialIndex) => {
          if (currentMaterialIndex !== materialIndex) return material;

          const qty = field === 'qty' ? value : Number(material.qty ?? material.quantity ?? 0);
          const rate = field === 'rate' ? value : Number(material.rate ?? 0);

          return {
            ...material,
            qty,
            rate,
            amount: qty * rate,
          };
        });

        return { ...ac, materials };
      }),
    );
  };

  const handleSave = async () => {
    if (!booking?._id) return;

    setSaving(true);
    try {
      await UpdateServiceReport(booking._id, {
        acs: draftAcs,
        totalAmount:
          draftPayment.totalAmount === '' ? undefined : Number(draftPayment.totalAmount),
        paidAmount:
          draftPayment.paidAmount === '' ? undefined : Number(draftPayment.paidAmount),
        paymentStatus: draftPayment.paymentStatus,
      });
      showSnackbar('Service report updated successfully', 'success');
      setIsEditing(false);
      onReload?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to update service report';
      showSnackbar(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const displayAcs = isEditing ? draftAcs : acs;
  const displayTotalAmount = isEditing ? draftPayment.totalAmount : totalAmount;
  const displayPaidAmount = isEditing ? draftPayment.paidAmount : paidAmount;
  const displayPaymentStatus = isEditing
    ? draftPayment.paymentStatus
    : serviceReport?.paymentStatus;
  const displayDueAmount =
    typeof displayTotalAmount === 'number' && typeof displayPaidAmount === 'number'
      ? Math.max(0, displayTotalAmount - displayPaidAmount)
      : dueAmount;

  return (
    <Box sx={{ backgroundColor: 'transparent', color: '#fff', borderRadius: 2 }}>
      {SnackbarComponent}
      <CardHeader
        title={<Typography variant="h6">Service Report</Typography>}
        action={
          serviceReport && acs.length > 0 ? (
            isEditing ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <SecondaryButton
                  title="Cancel"
                  onClick={cancelEditing}
                  disabled={saving}
                />
                <PrimaryButton
                  title={saving ? 'Saving...' : 'Save'}
                  onClick={handleSave}
                  disabled={saving}
                />
              </Box>
            ) : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={startEditing}
                sx={{
                  color: '#fff',
                  borderColor: '#555',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#E31E25',
                    backgroundColor: 'rgba(227, 30, 37, 0.08)',
                  },
                }}
              >
                Edit
              </Button>
            )
          ) : null
        }
      />
      <Divider sx={{ borderColor: '#565656' }} />

      <CardContent sx={{ pt: 2 }}>
        {displayAcs.length > 0 ? (
          displayAcs.map((ac, index) => (
            <BookingAccordion
              key={`${ac.serviceDetailId || 'ac'}-${index}`}
              ac={ac}
              index={index}
              editable={isEditing}
              onFieldChange={handleFieldChange}
              onMaterialChange={handleMaterialChange}
            />
          ))
        ) : (
          <Typography sx={{ color: '#8A8D91' }}>
            No AC service report available.
          </Typography>
        )}

        {serviceReport && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #333' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Payment Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                    Payment Status
                  </Typography>
                  {isEditing ? (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={draftPayment.paymentStatus}
                      onChange={(event) =>
                        setDraftPayment((prev) => ({
                          ...prev,
                          paymentStatus: event.target
                            .value as PaymentDraft['paymentStatus'],
                        }))
                      }
                      SelectProps={{ native: true }}
                      sx={fieldInputSx}
                    >
                      <option value="PENDING">Pending</option>
                      <option value="PARTIAL">Partial</option>
                      <option value="COMPLETE">Complete</option>
                    </TextField>
                  ) : (
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {String(displayPaymentStatus || 'N/A')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                    Total Amount
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={draftPayment.totalAmount}
                      onChange={(event) =>
                        setDraftPayment((prev) => ({
                          ...prev,
                          totalAmount:
                            event.target.value === ''
                              ? ''
                              : Number(event.target.value),
                        }))
                      }
                      sx={fieldInputSx}
                    />
                  ) : (
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {displayTotalAmount ?? 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                    Paid Amount
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={draftPayment.paidAmount}
                      onChange={(event) =>
                        setDraftPayment((prev) => ({
                          ...prev,
                          paidAmount:
                            event.target.value === ''
                              ? ''
                              : Number(event.target.value),
                        }))
                      }
                      sx={fieldInputSx}
                    />
                  ) : (
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {displayPaidAmount ?? 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                    Due Amount
                  </Typography>
                  <Typography sx={{ color: '#FFFFFF' }}>
                    {displayDueAmount !== null ? displayDueAmount : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography sx={{ color: '#BDBDBD', fontSize: '0.85rem' }}>
                    Material Amount (Technician)
                  </Typography>
                  <Typography sx={{ color: '#FFFFFF' }}>
                    {totalUserMaterialAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Box>
  );
};
